import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicPageHeader from '@components/common/PublicPageHeader';
import { LayoutGrid, Table as TableIcon, Star, Info, Compass, Calendar } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import { panchangService } from '@services/panchangService';
import KundliChart from '../free-kundli/components/KundliChart';
import { useAuthStore } from '@store/authStore';
import { useSettingsStore } from '@store/settingsStore';
import toast from 'react-hot-toast';
import { PANCHANG_TRANSLATIONS } from '../../../languages/panchang';
import { PAGE_SCHEMAS, SEOHead, getDynamicMetaTags } from '@/seo';
import {
    SunPlanetIcon,
    MoonPlanetIcon,
    MarsPlanetIcon,
    MercuryPlanetIcon,
    JupiterPlanetIcon,
    VenusPlanetIcon,
    SaturnPlanetIcon,
    RahuPlanetIcon,
    KetuPlanetIcon,
    AscendantIcon,
    UranusPlanetIcon,
    NeptunePlanetIcon,
    PlutoPlanetIcon
} from '../free-kundli/components/AstroIcons';

// Components
import ControlPanel from './components/ControlPanel';
import PanchangOverview from './components/PanchangOverview';
import SunMoonCalculations from './components/SunMoonCalculations';
import HinduCalendar from './components/HinduCalendar';
import Festivals from './components/Festivals';
import TimingsSection from './components/TimingsSection';
import SectionHeader from './components/SectionHeader';

const CHOGHADIYA_KEY_MAP: Record<string, string> = {
    Amrit: 'amrit', Shubh: 'shubh', Labh: 'labh',
    Chara: 'chara', Udvega: 'udvega', Roga: 'roga', Kala: 'kala',
};
const DINA_STATUS_KEY_MAP: Record<string, string> = {
    Excellent: 'excellent', Good: 'good', Average: 'average', Caution: 'caution',
};
const RASHI_MAP: { [key: string]: number } = {
    Mesha: 1, Vrishabha: 2, Mithuna: 3, Karka: 4, Simha: 5, Kanya: 6,
    Tula: 7, Vrishchika: 8, Vrischika: 8, Dhanu: 9, Makara: 10, Kumbha: 11, Meena: 12,
    Aries: 1, Taurus: 2, Gemini: 3, Cancer: 4, Leo: 5, Virgo: 6,
    Libra: 7, Scorpio: 8, Sagittarius: 9, Capricorn: 10, Aquarius: 11, Pisces: 12,
    'मेष': 1, 'वृषभ': 2, 'मिथुन': 3, 'कर्क': 4, 'सिंह': 5, 'कन्या': 6,
    'तुला': 7, 'वृश्चिक': 8, 'धनु': 9, 'मकर': 10, 'कुम्भ': 11, 'मीन': 12
};

const Panchang = () => {
    const { lang: urlLang, date: urlDate, locationName: urlLocationName, lat: urlLat, lon: urlLon } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuthStore();

    // State
    const [date, setDate] = useState(() => {
        if (urlDate) return urlDate;
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });
    const [location, setLocation] = useState({
        name: urlLocationName && urlLocationName !== 'Location'
            ? decodeURIComponent(urlLocationName).replace(/-/g, ' ')
            : urlLocationName === 'Location' ? '' : 'New Delhi, India',
        latitude: urlLat ? parseFloat(urlLat) : 28.6139,
        longitude: urlLon ? parseFloat(urlLon) : 77.2090
    });
    const { contentLanguage, setContentLanguage } = useSettingsStore();

    // Language state - initialized from URL param, Zustand store settings
    const [language, setLanguage] = useState<'english' | 'hindi'>(() => {
        if (urlLang === 'hindi' || urlLang === 'english') {
            return urlLang as 'english' | 'hindi';
        }
        if (user?.content_language) {
            const lang = user.content_language.toLowerCase();
            return (lang === 'hindi' ? 'hindi' : 'english') as 'english' | 'hindi';
        }
        return contentLanguage;
    });

    // Listen to Zustand store changes for language
    useEffect(() => {
        let storeLang: 'english' | 'hindi' = contentLanguage;

        if (user?.content_language) {
            const userLang = user.content_language.toLowerCase();
            storeLang = (userLang === 'hindi' ? 'hindi' : 'english') as 'english' | 'hindi';
        }

        if (storeLang !== language) {
            setLanguage(storeLang);
        }
    }, [user?.content_language, contentLanguage]);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);
    const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
    const isInitialMount = useRef(true);

    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = PANCHANG_TRANSLATIONS[langKey];

    // Update state when URL params change
    useEffect(() => {
        if (urlLang && urlLang !== language) setLanguage(urlLang as 'english' | 'hindi');
        if (urlDate && urlDate !== date) setDate(urlDate);
        if (urlLocationName) {
            const decodedName = urlLocationName === 'Location'
                ? ''
                : decodeURIComponent(urlLocationName).replace(/-/g, ' ');

            // If it's a coordinate being misparsed, ignore it
            if (/^-?\d+(\.\d+)?$/.test(decodedName) && decodedName !== '') {
                return;
            }

            if (decodedName !== location.name) {
                setLocation(prev => ({ ...prev, name: decodedName }));
            }
        }
        if (urlLat && urlLon) {
            const lat = parseFloat(urlLat);
            const lon = parseFloat(urlLon);
            if (lat !== location.latitude || lon !== location.longitude) {
                setLocation(prev => ({
                    ...prev,
                    latitude: lat,
                    longitude: lon
                }));
            }
        }
    }, [urlLang, urlDate, urlLocationName, urlLat, urlLon]);

    // Update URL when state changes
    useEffect(() => {
        const roundedLat = location.latitude.toFixed(4);
        const roundedLon = location.longitude.toFixed(4);
        const locationSlug = location.name
            ? encodeURIComponent(location.name.replace(/\s+/g, '-'))
            : 'Location';
        navigate(`/panchang/${language}/${date}/${locationSlug}/${roundedLat}/${roundedLon}`, { replace: true });
        setContentLanguage(language);
    }, [language, date, location.name, location.latitude, location.longitude, setContentLanguage, navigate]);

    // Fetch panchang data
    const fetchPanchang = async () => {
        setLoading(true);
        try {
            const response = await panchangService.getPanchang(
                date,
                location.latitude,
                location.longitude,
                language
            );

            if (response.success && response.panchang) {
                setData(response.panchang);
            }
        } catch (error) {
            console.error('Error fetching panchang:', error);
            toast.error('Failed to fetch panchang details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on initial mount and when language changes
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchPanchang();
        }
    }, []);

    // Fetch when language changes (after initial mount)
    useEffect(() => {
        if (!isInitialMount.current) {
            fetchPanchang();
        }
    }, [language]);

    // Handle language change from footer
    const handleLanguageChange = (lang: string) => {
        setLanguage(lang as 'english' | 'hindi');
    };

    // Helper function to convert 24-hour time to 12-hour AM/PM format
    const formatTime = (timeStr: string | null | undefined, dayOffset?: number | null): string => {
        if (!timeStr) return '---';
        try {
            const parts = timeStr.split(':');
            if (parts.length >= 2) {
                let hours = parseInt(parts[0], 10);
                const minutes = parts[1];
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12;
                hours = hours ? hours : 12;
                const timeLabel = `${hours}:${minutes} ${ampm}`;
                if (dayOffset && dayOffset > 0) {
                    return language === 'hindi'
                        ? `${timeLabel} (${t.nextDay})`
                        : `${timeLabel} (${t.nextDay})`;
                }
                return timeLabel;
            }
            return timeStr;
        } catch {
            return timeStr;
        }
    };

    // Format day duration
    const formatDuration = (duration: string | null | undefined): string => {
        if (!duration) return '---';
        const parts = duration.split(':');
        if (parts.length >= 2) {
            return `${parts[0]}h ${parts[1]}m`;
        }
        return duration;
    };

    const formatTimingRange = (timing?: { from?: string | null; to?: string | null } | null) => {
        if (!timing?.from || !timing?.to) return '---';
        const fromDayOffset = 'from_day_offset' in timing ? Number((timing as any).from_day_offset) : undefined;
        const toDayOffset = 'to_day_offset' in timing ? Number((timing as any).to_day_offset) : undefined;
        return language === 'hindi'
            ? `${formatTime(timing.from, fromDayOffset)} से ${formatTime(timing.to, toDayOffset)} तक`
            : `${formatTime(timing.from, fromDayOffset)} to ${formatTime(timing.to, toDayOffset)}`;
    };

    const translateTerm = (value: string | undefined, keyMap: Record<string, string>) => {
        const key = value ? keyMap[value] : undefined;
        return key ? (t[key] || value || '---') : (value || '---');
    };

    const translateChoghadiyaType = (type?: string) => translateTerm(type, CHOGHADIYA_KEY_MAP);
    const translateDinaStatus = (status?: string) => translateTerm(status, DINA_STATUS_KEY_MAP);
    // Helper function to get planet icon
    const getPlanetIcon = (name: string, className: string = "w-4 h-4") => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('sun') || lowerName.includes('surya') || lowerName.includes('सूर्य')) return <SunPlanetIcon className={className} />;
        if (lowerName.includes('moon') || lowerName.includes('chandra') || lowerName.includes('चंद्र')) return <MoonPlanetIcon className={className} />;
        if (lowerName.includes('mars') || lowerName.includes('mangal') || lowerName.includes('मंगल')) return <MarsPlanetIcon className={className} />;
        if (lowerName.includes('mercury') || lowerName.includes('budh') || lowerName.includes('बुध')) return <MercuryPlanetIcon className={className} />;
        if (lowerName.includes('jupiter') || lowerName.includes('guru') || lowerName.includes('brihaspati') || lowerName.includes('गुरु') || lowerName.includes('बृहस्पति')) return <JupiterPlanetIcon className={className} />;
        if (lowerName.includes('venus') || lowerName.includes('shukra') || lowerName.includes('शुक्र')) return <VenusPlanetIcon className={className} />;
        if (lowerName.includes('saturn') || lowerName.includes('shani') || lowerName.includes('शनि')) return <SaturnPlanetIcon className={className} />;
        if (lowerName.includes('rahu') || lowerName.includes('राहु')) return <RahuPlanetIcon className={className} />;
        if (lowerName.includes('ketu') || lowerName.includes('केतु')) return <KetuPlanetIcon className={className} />;
        if (lowerName.includes('uranus') || lowerName.includes('अरुण')) return <UranusPlanetIcon className={className} />;
        if (lowerName.includes('neptune') || lowerName.includes('वरुण')) return <NeptunePlanetIcon className={className} />;
        if (lowerName.includes('pluto') || lowerName.includes('यम')) return <PlutoPlanetIcon className={className} />;
        if (lowerName.includes('ascendant') || lowerName.includes('lagna') || lowerName.includes('लग्न')) return <AscendantIcon className={className} />;
        return <Star size={16} className="text-purple-400" />;
    };


    // Format degree from "DD-MM-SS" to "DD°MM'SS""
    const formatDegree = (dms: string | null | undefined): string => {
        if (!dms) return '---';
        const parts = dms.split('-');
        if (parts.length === 3) {
            return `${parseInt(parts[0])}°${parts[1]}'${parts[2]}"`;
        }
        return dms;
    };

    return (
        <div className="min-h-screen bg-cosmic-950 text-purple-50 font-sans selection:bg-purple-900 selection:text-purple-100 relative pb-16 md:pb-0">
            {/* SEO Meta Tags */}
            <SEOHead
                {...getDynamicMetaTags(
                    'panchang',
                    language === 'hindi' ? 'Hindi' : 'English',
                    {
                        date,
                        locationName: location.name || 'Your Location'
                    }
                )}
                language={language === 'hindi' ? 'hi' : 'en'}
                structuredData={PAGE_SCHEMAS.panchang}
            />

            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-16 md:pb-24 px-4 sm:px-6">
                <div className="container mx-auto max-w-5xl">
                    <PublicPageHeader
                        title={t.vedicPanchang}
                        highlightedText={t.vedicPanchang}
                        description={t.panchangDesc}
                        icon={Calendar}
                    />

                    <ControlPanel
                        date={date}
                        location={location}
                        loading={loading}
                        onDateChange={setDate}
                        onLocationChange={setLocation}
                        onFetchPanchang={fetchPanchang}
                        language={language}
                    />

                    {!data && loading ? (
                        <div className="py-20 text-center">
                            <div className="text-purple-400 animate-pulse font-sans">{t.consultingStars}</div>
                        </div>
                    ) : data ? (
                        <div className={`space-y-8 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                            <PanchangOverview
                                panchangFor={data.panchang_for}
                                day={data.day}
                                location={location}
                                tithi={data.tithi}
                                nakshatra={data.nakshatra}
                                yoga={data.yoga}
                                formatTime={formatTime}
                                language={language}
                            />

                            <SunMoonCalculations
                                sunMoon={data.sun_moon}
                                dayDuration={data.day_duration}
                                formatTime={formatTime}
                                formatDuration={formatDuration}
                                language={language}
                            />

                            <HinduCalendar hinduCalendar={data.hindu_calendar} language={language} />

                            <Festivals festivals={data.festivals} language={language} />

                            <TimingsSection
                                auspiciousTimings={data.auspicious_timings}
                                inauspiciousTimings={data.inauspicious_timings}
                                formatTime={formatTime}
                                language={language}
                            />

                            {/* Special Yogas */}
                            <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                <SectionHeader icon={Compass} title={t.specialYogas} />
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {[
                                        { label: t.bhadra, active: data.bhadra?.active, range: formatTimingRange(data.bhadra) },
                                        { label: t.panchaka, active: data.special_yogas?.panchaka },
                                        { label: t.gandaMula, active: data.special_yogas?.ganda_mula },
                                        { label: t.pushyaYoga, active: data.special_yogas?.pushya },
                                        { label: t.guruPushya, active: data.special_yogas?.guru_pushya },
                                        { label: t.raviPushya, active: data.special_yogas?.ravi_pushya },
                                        { label: t.siddhiClass, active: data.special_yogas?.siddhi_class },
                                    ].map((item) => (
                                        <div key={item.label} className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                            <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{item.label}</p>
                                            <p className={`text-sm md:text-base font-bold font-sans ${item.active ? 'text-green-400' : 'text-purple-200'}`}>
                                                {item.active ? t.active : t.inactive}
                                            </p>
                                            {'range' in item && item.range && item.active && (
                                                <p className="text-[10px] md:text-xs text-yellow-400 mt-1 font-sans">{item.range}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Dina Shuddhi */}
                            <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                <SectionHeader icon={Info} title={t.dinaShuddhi} />
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.score}</p>
                                        <p className="text-lg md:text-xl font-bold text-white font-sans">{data.dina_shuddhi?.score ?? '---'}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.status}</p>
                                        <p className="text-lg md:text-xl font-bold text-emerald-300 font-sans">{translateDinaStatus(data.dina_shuddhi?.status)}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.madhyahna}</p>
                                        <p className="text-sm md:text-base font-bold text-white font-sans">{formatTime(data.special_periods?.madhyahna)}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.ratrimana}</p>
                                        <p className="text-sm md:text-base font-bold text-white font-sans">{formatDuration(data.special_periods?.ratrimana)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Panchaka Rahita */}
                            <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                <SectionHeader icon={Calendar} title={t.panchakaRahita} />
                                <div className="space-y-4">
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-3 font-sans">{t.availableWindows}</p>
                                        {data.panchaka_rahita?.panchaka_active === false ? (
                                            <p className="text-sm text-green-400/80 font-sans">{t.panchakaNotActive}</p>
                                        ) : data.panchaka_rahita?.windows?.length > 0 ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                                {data.panchaka_rahita.windows.map((window: any, index: number) => (
                                                    <div key={index} className="flex items-center justify-between gap-3 rounded-lg bg-white/5 px-3 py-2">
                                                        <p className="text-sm font-bold text-white font-sans">{window.lagna}</p>
                                                        <span className="text-xs text-purple-200 font-sans">{formatTimingRange(window)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-purple-300/70 font-sans">{t.noRahitaWindow}</p>
                                        )}
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-3 font-sans">{t.udayaLagna}</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                            {data.udaya_lagna?.slice(0, 6).map((window: any, index: number) => (
                                                <div key={index} className="flex items-center justify-between gap-3 rounded-lg bg-white/5 px-3 py-2">
                                                    <p className="text-sm font-bold text-white font-sans">{window.lagna}</p>
                                                    <span className="text-xs text-purple-200 font-sans">{formatTimingRange(window)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Varjyam & Special Periods */}
                            <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                <SectionHeader icon={Star} title={t.varjyam} />
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.varjyam}</p>
                                        <p className="text-sm md:text-base font-bold text-white font-sans">{data.special_periods?.varjyam ? formatTimingRange(data.special_periods.varjyam) : t.noSpecialWindow}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.amritKalam}</p>
                                        <p className="text-sm md:text-base font-bold text-white font-sans">{data.special_periods?.amrit_kalam ? formatTimingRange(data.special_periods.amrit_kalam) : t.noSpecialWindow}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.vijayaMuhurta}</p>
                                        <p className="text-sm md:text-base font-bold text-white font-sans">{formatTimingRange(data.special_periods?.vijaya_muhurta)}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">{t.sayahnaSandhya}</p>
                                        <p className="text-sm md:text-base font-bold text-white font-sans">{formatTimingRange(data.special_periods?.sayahna_sandhya)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Day Choghadiya */}
                            <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                <SectionHeader icon={LayoutGrid} title={t.dayChoghadiya} />
                                {data.day_choghadiya?.length ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {data.day_choghadiya.map((item: any, index: number) => (
                                            <div key={index} className="bg-white/[0.08] border border-white/20 rounded-xl p-3">
                                                <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 font-sans">
                                                    {translateChoghadiyaType(item.type)}
                                                </p>
                                                <p className="text-sm font-bold text-white font-sans">{formatTimingRange(item)}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-purple-300/70 font-sans">{t.noDayChoghadiya}</p>
                                )}
                            </div>

                            {/* Planetary Positions */}
                            {data.planetary_position_at_sunrise && data.planetary_position_at_sunrise.length > 0 && (
                                <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 overflow-hidden shadow-xl">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                                        <SectionHeader icon={Star} title={t.planetaryPositionsSunrise} className="mb-0" />

                                        <div className="flex bg-white/[0.08] p-1 rounded-lg border border-white/20 w-full md:w-auto">
                                            <button
                                                onClick={() => setViewMode('chart')}
                                                className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors font-sans ${viewMode === 'chart'
                                                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/30'
                                                    : 'text-purple-300 hover:bg-white/5'
                                                    }`}
                                            >
                                                <LayoutGrid size={14} className="md:w-4 md:h-4" />
                                                {t.lagnaChart}
                                            </button>
                                            <button
                                                onClick={() => setViewMode('table')}
                                                className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors font-sans ${viewMode === 'table'
                                                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/30'
                                                    : 'text-purple-300 hover:bg-white/5'
                                                    }`}
                                            >
                                                <TableIcon size={14} className="md:w-4 md:h-4" />
                                                {t.tableView}
                                            </button>
                                        </div>
                                    </div>

                                    {viewMode === 'chart' ? (
                                        <div className="py-4">
                                            <KundliChart planets={data.planetary_position_at_sunrise} />
                                            <p className="text-center text-purple-300/60 text-xs mt-4 font-sans">
                                                {t.northIndianStyle}
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Desktop Table View */}
                                            <div className="hidden md:block overflow-x-auto">
                                                <table className="w-full text-left border-collapse">
                                                    <thead>
                                                        <tr className="bg-white/5 border-b border-white/20 text-xs text-purple-300 uppercase tracking-wider">
                                                            <th className="p-4 font-bold font-sans">{t.planet}</th>
                                                            <th className="p-4 font-bold text-center font-sans">{t.house}</th>
                                                            <th className="p-4 font-bold text-center font-sans">{t.rashi}</th>
                                                            <th className="p-4 font-bold text-center font-sans">{t.degree}</th>
                                                            <th className="p-4 font-bold text-center font-sans">{t.nakshatra}</th>
                                                            <th className="p-4 font-bold text-center font-sans">{t.pada}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-white/5">
                                                        {data.planetary_position_at_sunrise.map((planet: any, index: number) => {
                                                            const houseNum = RASHI_MAP[planet.rashi] || '---';
                                                            return (
                                                                <tr key={index} className="hover:bg-white/5 transition-colors">
                                                                    <td className="p-4">
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                                                                                {getPlanetIcon(planet.planet, "w-5 h-5")}
                                                                            </div>
                                                                            <span className="font-bold text-white text-sm">{planet.planet}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="p-4 text-purple-200 text-center font-bold font-sans">{houseNum}</td>
                                                                    <td className="p-4 text-purple-200 text-center font-sans">{planet.rashi}</td>
                                                                    <td className="p-4 text-purple-200 text-center font-mono text-xs">{formatDegree(planet.longitude)}</td>
                                                                    <td className="p-4 text-purple-200 text-center font-sans">{planet.nakshatra}</td>
                                                                    <td className="p-4 text-purple-200 text-center font-sans">{planet.pada}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Mobile Card View */}
                                            <div className="md:hidden divide-y divide-white/10">
                                                {data.planetary_position_at_sunrise.map((planet: any, index: number) => {
                                                    const houseNum = RASHI_MAP[planet.rashi] || '---';
                                                    return (
                                                        <div key={index} className="p-4">
                                                            <div className="flex items-center gap-3 mb-3">
                                                                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                                                    {getPlanetIcon(planet.planet, "w-6 h-6")}
                                                                </div>
                                                                <div>
                                                                    <span className="font-bold text-white">{planet.planet}</span>
                                                                    <p className="text-xs text-purple-200">{planet.rashi} • {t.house} {houseNum}</p>
                                                                </div>
                                                            </div>
                                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                                <div className="bg-white/5 rounded-lg p-2">
                                                                    <span className="text-[9px] text-purple-200/40 block">{t.degree}</span>
                                                                    <span className="text-xs font-bold text-white">{formatDegree(planet.longitude)}</span>
                                                                </div>
                                                                <div className="bg-white/5 rounded-lg p-2">
                                                                    <span className="text-[9px] text-purple-200/40 block">{t.nakshatra}</span>
                                                                    <span className="text-xs font-bold text-white truncate block">{planet.nakshatra || '---'}</span>
                                                                </div>
                                                                <div className="bg-white/5 rounded-lg p-2">
                                                                    <span className="text-[9px] text-purple-200/40 block">{t.pada}</span>
                                                                    <span className="text-xs font-bold text-white">{planet.pada || '---'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Additional Info */}
                            <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                <SectionHeader icon={Info} title={t.additionalInfo} />
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4 hover:bg-white/[0.12] transition-colors">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 md:mb-2 font-sans">{t.ascendant}</p>
                                        <p className="text-base md:text-lg font-bold text-violet-400 font-sans">{data.sun_moon?.ascendant || '---'}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4 hover:bg-white/[0.12] transition-colors">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 md:mb-2 font-sans">{t.ritu}</p>
                                        <p className="text-base md:text-lg font-bold text-white font-sans">{data.ritu || data.hindu_calendar?.ritu || '---'}</p>
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4 hover:bg-white/[0.12] transition-colors">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 md:mb-2 font-sans">{t.karana}</p>
                                        <p className="text-base md:text-lg font-bold text-white font-sans">{data.karana?.name || '---'}</p>
                                        {data.karana?.upto && <p className="text-[10px] md:text-sm text-yellow-400 font-sans">({language === 'hindi' ? `${formatTime(data.karana.upto)} ${t.upto}` : `${t.upto} ${formatTime(data.karana.upto)}`})</p>}
                                    </div>
                                    <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4 hover:bg-white/[0.12] transition-colors">
                                        <p className="text-[10px] md:text-xs text-purple-300/70 font-bold uppercase mb-1 md:mb-2 font-sans">{t.dishaShoola}</p>
                                        <p className="text-base md:text-lg font-bold text-white font-sans">{data.disha_shoola || '---'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Tara & Chandra Bala */}
                            {(data.chandrabalam_and_tarabal || data.tara_bala || data.chandra_bala) && (
                                <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
                                    <SectionHeader icon={Compass} title={t.taraChandraBala} />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {(data.chandrabalam_and_tarabal?.tara_bala || data.tara_bala) && (
                                            <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4 hover:bg-white/[0.12] transition-colors">
                                                <p className="text-xs text-purple-300/70 font-bold uppercase mb-2 font-sans">{t.taraBala}</p>
                                                <p className="text-white text-sm leading-relaxed font-sans">
                                                    {data.chandrabalam_and_tarabal?.tara_bala || data.tara_bala}
                                                </p>
                                            </div>
                                        )}
                                        {(data.chandrabalam_and_tarabal?.chandra_bala || data.chandra_bala) && (
                                            <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4 hover:bg-white/[0.12] transition-colors">
                                                <p className="text-xs text-purple-300/70 font-bold uppercase mb-2 font-sans">{t.chandraBala}</p>
                                                <p className="text-white text-sm leading-relaxed font-sans">
                                                    {data.chandrabalam_and_tarabal?.chandra_bala || data.chandra_bala}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </main >

            <Footer />
            <MobileBottomNav />
        </div >
    );
};

export default Panchang;
